import { CreateDealNotePayload, Note, UpdateCrmDealsNotePayload } from "types/crm/notes";
import { usePaginatedList } from "../usePaginatedList";
import { dealApi, dealQueryKeys } from "service/crm/api/deal";
import { useEntityCreateMutation } from "../useEntityMutation";
import { useMutation, useQueryClient } from "@tanstack/react-query";


export const useDealNotes = (dealUuid: string, pageSize = 20) => {
  const { items, ...rest } = usePaginatedList<Note>(
    dealQueryKeys.notes(dealUuid),
    (page) => dealApi.getDealNotes(dealUuid, page, pageSize),
    !!dealUuid,
  );
  return { notes: items, ...rest };
};

export const useCreateDealNoteMutation = () =>
  useEntityCreateMutation<CreateDealNotePayload, Note>(
    (payload) => dealApi.createDealNote(payload),
    (variables) => [
      dealQueryKeys.notes(variables.dealUuid),
      dealQueryKeys.activities(variables.dealUuid),
    ],
  );

export const useUpdateDealNoteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateCrmDealsNotePayload) =>
      dealApi.updateDealNote(payload),
    onSuccess: (_data, variables) => {
      // Prefix-match invalidation: clears the cache for every status filter
      // of this contact's task list, not just "ALL".
      queryClient.invalidateQueries({
        queryKey: [...dealQueryKeys.all, "notes", variables.dealUuid],
      });
      queryClient.invalidateQueries({
        queryKey: dealQueryKeys.activities(variables.dealUuid),
      });
    },
  });
};

export const useDeleteDealNoteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dealUuid,
      noteUuid,
    }: {
      dealUuid: string;
      noteUuid: string;
    }) => dealApi.deleteDealNote(dealUuid, noteUuid),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...dealQueryKeys.all, "notes", variables.dealUuid],
      });
      queryClient.invalidateQueries({
        queryKey: dealQueryKeys.activities(variables.dealUuid),
      });
    },
  });
};